import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import TextFieldV2 from 'core-system/TextFieldV2'
import FlexContainer from 'core-system/FlexContainer'
import pxToRem from 'core-system/utils/pxToRem'
import palette from 'core-system/Themes/palette'
import OnboardingTasksDrawer from '../components/OnboardingTasksDrawer'
import DatePicker from 'core-system/DatePicker'
import {
  validateRequiredField,
  validateEmail,
  validateSSNLast4,
  validateDateOfBirth,
  validatePhoneNumber,
  validateEIN,
  validateZipCode,
} from '../helperFunctions'
import { onboardingActions } from '../onboardingActions'
import onboardingService from '../onboardingService'
import { useDispatch } from 'react-redux'
import moment from 'moment'

const InputContainer = styled.div<{ inline?: boolean }>`
  margin-bottom: 1rem;
  border-radius: ${pxToRem('4')};
  ${(props) =>
    props.inline
      ? {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          '> div': {
            flex: 1,
          },
        }
      : null}
  @media (max-width: ${(props) => props.theme.breakpoints[0]}) {
    display: block;
    > div {
      max-width: 100%;
      margin-bottom: 1.5rem;
    }
  }
`

const FlexContainerWithBackground = styled(FlexContainer)`
  background-color: ${palette.secondary.purple100};
  padding: 1rem;
  border-color: ${palette.secondary.purple600};
  border-width: 1px;
  border-style: solid;
  border-radius: 0.4375rem;
  flex-direction: column;
  margin-bottom: 1rem;
`

const PurposeBox = styled.div`
  background-color: ${palette.primary.pink100};
  padding: 1rem;
  border-radius: 0.4375rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${palette.primary.pink300};
`

const InfoSection = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${palette.grey.grey100};
  border-radius: 0.4375rem;
  border: 1px solid ${palette.grey.grey200};
`

const InfoTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 0.75rem;
`

const InfoText = styled(Text)`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`

const BulletPoint = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`

const PrivacyLink = styled.span`
  text-decoration: underline;
  color: ${palette.primary.pink800};
  cursor: pointer;
`

interface KYCCsvProps {
  isOpen: boolean
  onClose: () => void
}

export interface KycCsvFormData {
  // Company Info
  legalBusinessName: string
  businessEIN: string
  businessPhoneNumber: string
  businessStreetAddress: string
  businessCity: string
  businessState: string
  businessZip: string

  // Representative Info
  firstName: string
  lastName: string
  email: string
  dateOfBirth: string | null
  title: string
  representativeStreetAddress: string
  representativeCity: string
  representativeState: string
  representativeZip: string
  ssnLastFour: string
}

const KYCCsv = (props: KYCCsvProps) => {
  const { isOpen, onClose } = props
  const dispatch = useDispatch()
  const [formData, setFormData] = useState<KycCsvFormData>({
    legalBusinessName: '',
    businessEIN: '',
    businessPhoneNumber: '',
    businessStreetAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: null,
    title: '',
    representativeStreetAddress: '',
    representativeCity: '',
    representativeState: '',
    representativeZip: '',
    ssnLastFour: '',
  })

  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: keyof KycCsvFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDateChange = (date: Date) => {
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: date,
    }))
  }

  const validateForm = (): boolean => {
    // Company Info validations
    if (
      !validateRequiredField(
        formData.legalBusinessName,
        'legal business name',
        setErrorMessage
      )
    ) {
      return false
    }
    if (!validateEIN(formData.businessEIN, setErrorMessage)) {
      return false
    }
    if (!validatePhoneNumber(formData.businessPhoneNumber, setErrorMessage)) {
      return false
    }
    if (
      !validateRequiredField(
        formData.businessStreetAddress,
        'business street address',
        setErrorMessage
      )
    ) {
      return false
    }
    if (
      !validateRequiredField(
        formData.businessCity,
        'business city',
        setErrorMessage
      )
    ) {
      return false
    }
    if (
      !validateRequiredField(
        formData.businessState,
        'business state',
        setErrorMessage
      )
    ) {
      return false
    }
    if (!validateZipCode(formData.businessZip, setErrorMessage)) {
      return false
    }

    // Representative Info validations
    if (
      !validateRequiredField(formData.firstName, 'first name', setErrorMessage)
    ) {
      return false
    }
    if (
      !validateRequiredField(formData.lastName, 'last name', setErrorMessage)
    ) {
      return false
    }
    if (!validateEmail(formData.email, setErrorMessage)) {
      return false
    }
    if (!validateDateOfBirth(formData.dateOfBirth, setErrorMessage)) {
      return false
    }
    if (!validateRequiredField(formData.title, 'title', setErrorMessage)) {
      return false
    }
    if (
      !validateRequiredField(
        formData.representativeStreetAddress,
        'representative street address',
        setErrorMessage
      )
    ) {
      return false
    }
    if (
      !validateRequiredField(
        formData.representativeCity,
        'representative city',
        setErrorMessage
      )
    ) {
      return false
    }
    if (
      !validateRequiredField(
        formData.representativeState,
        'representative state',
        setErrorMessage
      )
    ) {
      return false
    }
    if (!validateZipCode(formData.representativeZip, setErrorMessage)) {
      return false
    }
    if (!validateSSNLast4(formData.ssnLastFour, setErrorMessage)) {
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        return
      }
      setIsLoading(true)

      // Format the date to YYYY-MM-DD before sending to backend
      const formattedData = {
        ...formData,
        dateOfBirth: formData.dateOfBirth
          ? moment(formData.dateOfBirth).format('YYYY-MM-DD')
          : null,
      }

      const response =
        await onboardingService.createCsvKycDetailsService(formattedData)

      if (response.status === 200 || response.status === 201) {
        dispatch(
          onboardingActions.updateOnboardingSectionStatus({
            sectionType: 'KYC_VERIFICATION',
            status: 'IN_PROGRESS',
          })
        )
        onClose()
        setIsLoading(false)
      }
    } catch (error) {
      setErrorMessage('Error submitting KYC CSV details')
      setIsLoading(false)
    }
  }

  return (
    <OnboardingTasksDrawer
      isOpen={isOpen}
      onClose={onClose}
      primaryButton={{
        text: 'Submit KYC',
        onClick: handleSubmit,
        loading: isLoading,
      }}
    >
      <FlexContainer flexDirection='column'>
        {/* Purpose of Form */}
        <PurposeBox>
          <Text variant='body1' fontWeight={600} marginBottom='0.5rem'>
            Purpose of this Form:
          </Text>
          <Text variant='body2'>
            As part of our payment processing setup, Stripe (our payment
            processor) requires us to collect and verify the identity of
            individuals associated with our account. This is a mandatory
            requirement from Stripe's regulators and financial partners.
          </Text>
        </PurposeBox>

        {/* Company Info Section */}
        <FlexContainerWithBackground>
          <Text variant='h4' marginBottom='1rem' color={palette.text.primary}>
            Company Info
          </Text>

          <InputContainer>
            <TextFieldV2
              label='Legal Business Name'
              placeholder='Enter legal business name'
              name='legalBusinessName'
              type='text'
              onChange={(e) =>
                handleInputChange('legalBusinessName', e.target.value)
              }
              value={formData.legalBusinessName}
              required
            />
          </InputContainer>

          <InputContainer inline>
            <TextFieldV2
              label='Business EIN'
              placeholder='Enter EIN'
              name='businessEIN'
              type='text'
              onChange={(e) => handleInputChange('businessEIN', e.target.value)}
              value={formData.businessEIN}
              required
            />
            <TextFieldV2
              label='Business Phone Number'
              placeholder='Enter phone number'
              name='businessPhoneNumber'
              type='tel'
              onChange={(e) =>
                handleInputChange('businessPhoneNumber', e.target.value)
              }
              value={formData.businessPhoneNumber}
              required
            />
          </InputContainer>

          <InputContainer>
            <TextFieldV2
              label='Business Street Address'
              placeholder='Enter street address'
              name='businessStreetAddress'
              type='text'
              onChange={(e) =>
                handleInputChange('businessStreetAddress', e.target.value)
              }
              value={formData.businessStreetAddress}
              required
            />
          </InputContainer>

          <InputContainer inline>
            <TextFieldV2
              label='City'
              placeholder='Enter city'
              name='businessCity'
              type='text'
              onChange={(e) =>
                handleInputChange('businessCity', e.target.value)
              }
              value={formData.businessCity}
              required
            />
            <TextFieldV2
              label='State'
              placeholder='Enter state'
              name='businessState'
              type='text'
              onChange={(e) =>
                handleInputChange('businessState', e.target.value)
              }
              value={formData.businessState}
              required
            />
            <TextFieldV2
              label='Zip'
              placeholder='Enter zip code'
              name='businessZip'
              type='text'
              onChange={(e) => handleInputChange('businessZip', e.target.value)}
              value={formData.businessZip}
              required
            />
          </InputContainer>
        </FlexContainerWithBackground>

        {/* Representative Info Section */}
        <FlexContainerWithBackground>
          <Text variant='h4' marginBottom='1rem' color={palette.text.primary}>
            Representative Info
          </Text>

          <InputContainer inline>
            <TextFieldV2
              label='First Name'
              placeholder='Enter first name'
              name='firstName'
              type='text'
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              value={formData.firstName}
              required
            />
            <TextFieldV2
              label='Last Name'
              placeholder='Enter last name'
              name='lastName'
              type='text'
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              value={formData.lastName}
              required
            />
          </InputContainer>

          <InputContainer>
            <TextFieldV2
              label='Email'
              placeholder='Enter email address'
              name='email'
              type='email'
              onChange={(e) => handleInputChange('email', e.target.value)}
              value={formData.email}
              required
            />
          </InputContainer>

          <InputContainer>
            <Text
              variant='body2'
              marginBottom='0.5rem'
              color={palette.text.secondary}
            >
              Date of Birth
            </Text>
            <DatePicker
              id='date-picker'
              placeholderText='Select date'
              selected={formData.dateOfBirth}
              onChange={handleDateChange}
              dateFormat='MM/dd/yyyy'
              maxDate={new Date()}
              showYearDropdown
              showMonthDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              shouldCloseOnSelect={true}
            />
          </InputContainer>
          <InputContainer>
            <TextFieldV2
              label='Title'
              placeholder='Enter title'
              name='title'
              type='text'
              onChange={(e) => handleInputChange('title', e.target.value)}
              value={formData.title}
              required
            />
          </InputContainer>

          <InputContainer>
            <TextFieldV2
              label='Representative Street Address'
              placeholder='Enter street address'
              name='representativeStreetAddress'
              type='text'
              onChange={(e) =>
                handleInputChange('representativeStreetAddress', e.target.value)
              }
              value={formData.representativeStreetAddress}
              required
            />
          </InputContainer>

          <InputContainer inline>
            <TextFieldV2
              label='City'
              placeholder='Enter value'
              name='representativeCity'
              type='text'
              onChange={(e) =>
                handleInputChange('representativeCity', e.target.value)
              }
              value={formData.representativeCity}
              required
            />
            <TextFieldV2
              label='State'
              placeholder='Enter state'
              name='representativeState'
              type='text'
              onChange={(e) =>
                handleInputChange('representativeState', e.target.value)
              }
              value={formData.representativeState}
              required
            />
            <TextFieldV2
              label='Zip'
              placeholder='Enter zip code'
              name='representativeZip'
              type='text'
              onChange={(e) =>
                handleInputChange('representativeZip', e.target.value)
              }
              value={formData.representativeZip}
              required
            />
          </InputContainer>

          <InputContainer>
            <TextFieldV2
              label='Last 4 digits of Social Security number'
              placeholder='Enter last 4 digits'
              name='ssnLastFour'
              type='number'
              onChange={(e) => handleInputChange('ssnLastFour', e.target.value)}
              value={formData.ssnLastFour}
              required
            />
            <Text
              variant='body2'
              marginTop='0.5rem'
              color={palette.text.secondary}
            >
              We are required to collect this information to satisfy regulatory
              obligations.
            </Text>
          </InputContainer>
        </FlexContainerWithBackground>

        {/* Information Sections */}
        <InfoSection>
          <InfoTitle variant='h5' color={palette.text.primary}>
            How Stripe Handles Your Information:
          </InfoTitle>
          <InfoText variant='body2' color={palette.text.secondary}>
            Stripe is a PCI Level 1 compliant company (the most stringent level
            of certification available in the payments industry). Your sensitive
            information is handled as follows:
          </InfoText>
          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
            <BulletPoint>
              <InfoText variant='body2' color={palette.text.secondary}>
                <strong>Data Security:</strong> Your information is protected
                using industry-standard encryption and security measures.
              </InfoText>
            </BulletPoint>
            <BulletPoint>
              <InfoText variant='body2' color={palette.text.secondary}>
                <strong>Regulatory Compliance:</strong> Stripe complies with all
                applicable regulations including Anti-Money Laundering (AML)
                requirements.
              </InfoText>
            </BulletPoint>
            <BulletPoint>
              <InfoText variant='body2' color={palette.text.secondary}>
                <strong>Limited Access:</strong> Your Company does not see or
                store sensitive information such as your SSN; this information
                is solely held by Stripe.
              </InfoText>
            </BulletPoint>
            <BulletPoint>
              <InfoText variant='body2' color={palette.text.secondary}>
                <strong>Limited Purpose:</strong> This information is only used
                for identity verification purposes as required by financial
                regulators.
              </InfoText>
            </BulletPoint>
          </ul>
          <InfoText
            variant='body2'
            color={palette.text.secondary}
            marginTop='1rem'
          >
            For complete details on how Stripe handles your information, you can
            review Stripe's{' '}
            <PrivacyLink
              onClick={() =>
                window.open('https://stripe.com/privacy', '_blank')
              }
            >
              Privacy Policy
            </PrivacyLink>
            .
          </InfoText>
        </InfoSection>

        <InfoSection>
          <InfoTitle variant='h5' color={palette.text.primary}>
            Form Deletion Policy:
          </InfoTitle>
          <InfoText variant='body2' color={palette.text.secondary}>
            For your security, once we have entered your information into
            Stripe's secure system, this intake form and all copies will be
            permanently deleted from our records. We will not retain any copies
            of this form containing your sensitive information after processing
            is complete.
          </InfoText>
        </InfoSection>
        {errorMessage && (
          <Text variant='body1' marginTop='1rem' color={palette.error.red600}>
            {errorMessage}
          </Text>
        )}
      </FlexContainer>
    </OnboardingTasksDrawer>
  )
}

export default KYCCsv
